import { useState, useEffect } from 'react';
import { Settings, CheckCircle, XCircle, Loader, Upload, Database, Bot, RefreshCw } from 'lucide-react';
import { setupPRIsMEAssistant, getAssistantIds, isAssistantConfigured } from '../services/openaiAssistantSetup';
import { saveLastSyncDates } from '../services/lexLastSyncService';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import { isOpenAIConfigured } from '../services/openaiBackend';

const LEX_SYNC_KEYS = [
  'prisme_manifeste_last_ai_analysis',
  'prisme_leadership_last_ai_analysis',
  'prisme_overview_last_ai_analysis',
  'amelioration_continue_last_ai_analysis',
  'concepts_last_ai_analysis',
  'philosophie_croissance_last_ai_analysis',
  'methodologie_prisme_last_ai_analysis',
  'qui_est_bz_last_ai_analysis',
  'referentiel_prime_last_ai_analysis',
  'prise_besoin_ods_last_ai_analysis',
  'formation_gestion_documentaire_last_ai_analysis',
  'arbre_prisme_concept_last_ai_analysis',
  'code_rouge_last_ai_analysis',
  'gouvernance_comite_ti_last_ai_analysis',
  'cadres_nist_last_ai_analysis',
  'diagnostic_ti_last_ai_analysis',
  'referentiel_directeur_compte_last_ai_analysis',
  'guide_vente_prisme_last_ai_analysis',
  'gestion_projets_prisme_last_ai_analysis',
  'comite_ti_prisme_last_ai_analysis',
  'comite_ti_dashboard_clients_last_ai_analysis',
  'procedure_clone_prisme_client_last_ai_analysis',
  'gouvernance_prisme_last_ai_analysis',
  'creer_utilisateur_prisme_last_ai_analysis',
  'gouvernance_comite_securite_last_ai_analysis',
  'efvp_superhuman_mail_last_ai_analysis',
  'catalogue_actifs_last_ai_analysis',
  'lex_chatbot_prisme_last_ai_analysis',
  'solutions_affaires_last_ai_analysis',
  'responsabilites_ti_cyber_sa_last_ai_analysis',
] as const;

export default function AIAssistantSetupPanel() {
  const { refetch: refetchLexDates } = useLexLastSync();
  const [isSetupRunning, setIsSetupRunning] = useState(false);
  const [setupStatus, setSetupStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [setupMessage, setSetupMessage] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const [openAIConfigured, setOpenAIConfigured] = useState<boolean | null>(null);

  useEffect(() => {
    isOpenAIConfigured().then(setOpenAIConfigured);
  }, []);

  const isConfigured = isAssistantConfigured();
  const { assistantId, vectorStoreId } = getAssistantIds();

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const handleSetup = async () => {
    setIsSetupRunning(true);
    setSetupStatus('idle');
    setLogs([]);
    
    addLog('🚀 Début du setup PRIsME Assistant...');
    
    try {
      // Intercept console.log from setup functions
      const originalLog = console.log;
      console.log = (...args) => {
        addLog(args.join(' '));
        originalLog(...args);
      };

      const result = await setupPRIsMEAssistant();
      
      console.log = originalLog; // Restore

      if (result.success) {
        setSetupStatus('success');
        setSetupMessage(result.message);
        addLog(`✅ ${result.message}`);
        addLog(`📋 Assistant ID: ${result.assistantId}`);
        addLog(`📋 Vector Store ID: ${result.vectorStoreId}`);
        const analysisDate = new Date().toISOString();
        const dates: Record<string, string> = {};
        LEX_SYNC_KEYS.forEach((key) => { dates[key] = analysisDate; });
        try {
          await saveLastSyncDates(dates);
          await refetchLexDates();
          addLog(`📅 Dates LEX sauvegardées côté serveur (tous les usagers voient la même synchro).`);
        } catch (e) {
          addLog(`⚠️ Synchro LEX OK mais enregistrement des dates serveur échoué: ${e instanceof Error ? e.message : String(e)}`);
        }
      } else {
        setSetupStatus('error');
        setSetupMessage(result.message);
        addLog(`❌ ${result.message}`);
      }
    } catch (error) {
      setSetupStatus('error');
      const errorMsg = error instanceof Error ? error.message : 'Erreur inconnue';
      setSetupMessage(errorMsg);
      addLog(`❌ Erreur: ${errorMsg}`);
    } finally {
      setIsSetupRunning(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl text-white">
          <Settings className="w-8 h-8" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Configuration AI Assistant</h2>
          <p className="text-gray-600">Setup initial de l'Assistant PRIsME avec OpenAI</p>
        </div>
      </div>

      {/* Status actuel */}
      <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          {isConfigured ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          Statut Actuel
        </h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Clé API OpenAI (serveur):</span>
            <span className={`font-semibold ${openAIConfigured === null ? 'text-gray-500' : openAIConfigured ? 'text-green-600' : 'text-red-600'}`}>
              {openAIConfigured === null ? '…' : openAIConfigured ? '✓ Configurée' : '✗ Manquante'}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Assistant ID:</span>
            <span className={`font-mono text-sm ${assistantId ? 'text-green-600' : 'text-gray-400'}`}>
              {assistantId || 'Non créé'}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Vector Store ID:</span>
            <span className={`font-mono text-sm ${vectorStoreId ? 'text-green-600' : 'text-gray-400'}`}>
              {vectorStoreId || 'Non créé'}
            </span>
          </div>
        </div>
      </div>

      {/* Étapes du setup */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Étapes du Setup</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <Upload className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">1. Upload Knowledge Files</p>
              <p className="text-sm text-blue-700">5 fichiers Markdown (Méthodologie, Cadre BZ, NIST, Concepts, IA)</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <Database className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-semibold text-purple-900">2. Créer Vector Store</p>
              <p className="text-sm text-purple-700">Indexation sémantique pour recherche intelligente</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <Bot className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="font-semibold text-green-900">3. Créer Assistant</p>
              <p className="text-sm text-green-700">Configuration personnalité Dany + File Search activé</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-4 mb-8">
        <button
          onClick={handleSetup}
          disabled={isSetupRunning || openAIConfigured !== true}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
        >
          {isSetupRunning ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Setup en cours...
            </>
          ) : isConfigured ? (
            <>
              <RefreshCw className="w-5 h-5" />
              Re-créer Assistant
            </>
          ) : (
            <>
              <Bot className="w-5 h-5" />
              Lancer Setup Initial
            </>
          )}
        </button>
      </div>

      {/* Logs */}
      {logs.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Logs Setup</h3>
          <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm max-h-64 overflow-y-auto space-y-1">
            {logs.map((log, idx) => (
              <div key={idx}>{log}</div>
            ))}
          </div>
        </div>
      )}

      {/* Result message */}
      {setupStatus !== 'idle' && (
        <div className={`p-6 rounded-lg border ${
          setupStatus === 'success' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-start gap-3">
            {setupStatus === 'success' ? (
              <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
            ) : (
              <XCircle className="w-6 h-6 text-red-600 mt-0.5" />
            )}
            <div>
              <p className={`font-semibold ${
                setupStatus === 'success' ? 'text-green-900' : 'text-red-900'
              }`}>
                {setupStatus === 'success' ? 'Setup Réussi! 🎉' : 'Erreur Setup'}
              </p>
              <p className={`text-sm mt-1 ${
                setupStatus === 'success' ? 'text-green-700' : 'text-red-700'
              }`}>
                {setupMessage}
              </p>
              {setupStatus === 'success' && (
                <p className="text-sm text-green-600 mt-2">
                  ✅ Tu peux maintenant utiliser l'AI Assistant! (Bouton en bas à droite)
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help */}
      {openAIConfigured === false && (
        <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="font-semibold text-amber-900 mb-2">⚠️ Clé API OpenAI manquante côté serveur</h4>
          <p className="text-sm text-amber-700">
            Configure <code className="bg-amber-100 px-2 py-1 rounded">OPENAI_API_KEY</code> dans les variables d&apos;environnement du serveur (ex. <code className="bg-amber-100 px-2 py-1 rounded">.env</code> ou Railway).
          </p>
          <p className="text-sm text-amber-700 mt-2">
            La clé ne doit jamais être exposée dans le frontend.
          </p>
        </div>
      )}
    </div>
  );
}
